import Game from '../game';
import BaseObject, { BaseObjectParams } from '../baseObject';
import Scene from '../scene/scene';
import Camera from '../camera';
import Element from '../element/element';

import { CoordsType } from '../../utils/geometry';
import { Iterator } from '../../utils/types';


export interface ControlParams extends BaseObjectParams {
  canvas?: HTMLElement;
  scene?: Scene;
  context: CanvasRenderingContext2D;
}

export default abstract class Control extends BaseObject implements ControlParams {
  canvas: HTMLElement;
  context: CanvasRenderingContext2D;

  enabled = false;

  onMouseLeave!: (e: MouseEvent) => void;
  onMouseMove!: (e: MouseEvent) => void;

  onMouseDown!: (e: MouseEvent) => void;
  onMouseUp!: (e: MouseEvent) => void;

  onMouseWheel!: (e: WheelEvent) => void;
  onContextMenu = (e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
  }

  onKeyDown!: (e: KeyboardEvent) => void;
  onKeyUp!: (e: KeyboardEvent) => void;

  constructor(params: ControlParams) {
    super();
    const { context } = params;
    this.context = context;
    this.canvas = this.context.canvas;
  }

  get eventListenerTarget(): HTMLElement {
    return Game.instance.containerDOM;
  }

  get scene(): Scene {
    return Game.instance.scene;
  }

  draw(): void { }

  enable(): Control {
    this.enabled = true;
    const { eventListenerTarget: target } = this;

    target.addEventListener('mousemove', this.onMouseMove);
    target.addEventListener('mouseleave', this.onMouseLeave);

    target.addEventListener('mousedown', this.onMouseDown);
    target.addEventListener('mouseup', this.onMouseUp);

    target.addEventListener('wheel', this.onMouseWheel);
    target.addEventListener('contextmenu', this.onContextMenu);

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    return this;
  }

  disable(): Control {
    this.enabled = false;
    const { eventListenerTarget: target } = this;

    target.removeEventListener('mousemove', this.onMouseMove);
    target.removeEventListener('mouseleave', this.onMouseLeave);

    target.removeEventListener('mousedown', this.onMouseDown);
    target.removeEventListener('mouseup', this.onMouseUp);

    target.removeEventListener('wheel', this.onMouseWheel);
    target.removeEventListener('contextmenu', this.onContextMenu);

    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    return this;
  }

  getCamera(): Camera | undefined {
    return this?.scene?.camera;
  }

  getPointerCoords(e: MouseEvent, ignoreCamera = false): { x: number, y: number } {
    const domElem = e.target as HTMLElement;
    const boundRect = domElem.getBoundingClientRect();

    let ratioX = 1, ratioY = 1;
    if (
      domElem.getAttribute('width') !== undefined
      && domElem.getAttribute('height') !== undefined
    ) {
      ratioX = parseFloat(domElem.getAttribute('width') as string) / boundRect.width;
      ratioY = parseFloat(domElem.getAttribute('height') as string) / boundRect.height;
    }

    let x = (e.clientX - boundRect.left) * ratioX / this.scene.zoomRatio;
    let y = (e.clientY - boundRect.top) * ratioY / this.scene.zoomRatio;

    /* Camera adaptation */
    if (!ignoreCamera) {
      const camera = this.getCamera();
      if (camera) {
        x += camera.x - camera.width / 2;
        y += camera.y - camera.height / 2;
      }
    }

    return { x, y };
  }

  getCoordsForMultipleElements(baseCoords: CoordsType, elements: Array<Element>): Iterator<CoordsType> {
    if (elements.length === 1)
      return {
        [elements[0].id]: {
          x: baseCoords.x,
          y: baseCoords.y,
        }
      };

    // Get the maximum element size for spacing
    const spaceBetweenElements = elements.reduce((acc: number, current: Element) => {
      return Math.max(acc, current.width ?? 0, current.height ?? 0);
    }, 0);

    const positions = this.getConcentricCirclePositions(
      baseCoords.x,
      baseCoords.y,
      spaceBetweenElements,
      elements.length
    );

    const orderedElements = elements.sort((a, b) => {
      return (b.width ?? 0) - (a.width ?? 0); // Biggest ships first (near the center)
    });

    const retCoords: Iterator<CoordsType> = {};
    orderedElements.forEach((elem: Element, index: number) => {
      retCoords[elem.id] = positions[index];
    });

    return retCoords;
  }

  /* Concentric circle packing for element positioning */
  private getConcentricCirclePositions(
    cx: number,
    cy: number,
    elementSpacing: number,
    n: number
  ): CoordsType[] {
    const positions: CoordsType[] = [];

    // Ring 0: center (1 element)
    if (n > 0)
      positions.push({ x: cx, y: cy });

    let remaining = n - 1;
    let ring = 1;

    while (remaining > 0) {
      // Elements in this ring: ring * 6 (hexagonal packing)
      const elementsInRing = Math.min(ring * 6, remaining);
      const radius = ring * elementSpacing;

      for (let i = 0; i < elementsInRing; i++) {
        const angle = (2 * Math.PI * i) / elementsInRing - Math.PI / 2;
        positions.push({
          x: cx + radius * Math.cos(angle),
          y: cy + radius * Math.sin(angle),
        });
      }

      remaining -= elementsInRing;
      ring++;
    }

    return positions;
  }
}
